import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import { tokenIsExpired } from '../../../authUtils';
import {
    Refresh, FileDownload
} from '@mui/icons-material';
import {
    Button, Tooltip, Box, Typography, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton
} from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const CustomerAgingReport = () => {
    const { isAuthenticated, companyId, token, logout, username, companyName } = useAuth();

    // Data states
    const [customerDueData, setCustomerDueData] = useState([]);
    const [agingData, setAgingData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        if (companyId && token) {
            fetchCustomerDueData();
        }
    }, [companyId, token]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    // Calculate aging buckets
    const calculateAgingBuckets = (data) => {
        const customerMap = new Map();

        data.forEach(item => {
            const customerName = item.CustomerName || 'Unknown';
            const balanceAmount = parseFloat(item.BalanceAmount) || 0;
            const dueDays = parseInt(item.DueDays) || 0;

            if (!customerMap.has(customerName)) {
                customerMap.set(customerName, {
                    customerName: customerName,
                    lessThan30: 0,
                    days30to45: 0,
                    days45to60: 0,
                    days60to90: 0,
                    moreThan90: 0,
                    total: 0
                });
            }

            const customer = customerMap.get(customerName);

            // Categorize by due days
            if (dueDays < 30) {
                customer.lessThan30 += balanceAmount;
            } else if (dueDays >= 30 && dueDays < 45) {
                customer.days30to45 += balanceAmount;
            } else if (dueDays >= 45 && dueDays < 60) {
                customer.days45to60 += balanceAmount;
            } else if (dueDays >= 60 && dueDays < 90) {
                customer.days60to90 += balanceAmount;
            } else {
                customer.moreThan90 += balanceAmount;
            }

            customer.total += balanceAmount;
        });

        return Array.from(customerMap.values()).sort((a, b) => 
            a.customerName.localeCompare(b.customerName)
        );
    };

    // Fetch customer due list data
    const fetchCustomerDueData = async () => {
        setCustomerDueData([]);
        setAgingData([]);
        let loadingTimeout;
        setLoadingMessage('Loading customer ageing report...');
        try {
            loadingTimeout = setTimeout(() => {
                setLoading(true);
            }, 300);

            const currentDate = getFormattedCurrentDate();
            const response = await fetchData('ReportApi', `/CustomerDuelist/${currentDate}/${companyId}/0`, token);
            
            if (response) {
                let data = [];
                if (response.responseCode === '1111') {
                    data = response.response_data || [];
                } else if (Array.isArray(response)) {
                    data = response;
                } else if (response.response_data) {
                    data = response.response_data || [];
                }
                
                setCustomerDueData(data);
                const aging = calculateAgingBuckets(data);
                setAgingData(aging);
            }
        } catch (error) {
            console.error('Error fetching customer ageing report:', error);
            setCustomerDueData([]);
            setAgingData([]);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        if (agingData.length === 0) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Customer Ageing Report');
        worksheet.views = [
            { state: 'frozen', xSplit: 0, ySplit: 3 }
        ];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Customer_Aging_Report_${now.toLocaleDateString().replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };

        // Company Name Row
        worksheet.mergeCells('A1:G1');
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14 };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Title row
        worksheet.mergeCells('A2:G2');
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Customer Ageing Report | Generated On: ${formattedDateTime} By: ${username}`;
        titleCell.font = { bold: true, size: 10 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Header row
        const headers = ['Customer Name', '< 30 Days', '30-45 Days', '45-60 Days', '60-90 Days', '> 90 Days', 'Total'];
        const headerRow = worksheet.addRow(headers);

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7b3f8f' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = thinBorder;
        });

        // Calculate totals
        let totalLessThan30 = 0;
        let total30to45 = 0;
        let total45to60 = 0;
        let total60to90 = 0;
        let totalMoreThan90 = 0;
        let grandTotal = 0;

        // Add data rows
        agingData.forEach((rowData) => {
            totalLessThan30 += rowData.lessThan30;
            total30to45 += rowData.days30to45;
            total45to60 += rowData.days45to60;
            total60to90 += rowData.days60to90;
            totalMoreThan90 += rowData.moreThan90;
            grandTotal += rowData.total;

            const row = worksheet.addRow([
                rowData.customerName,
                rowData.lessThan30,
                rowData.days30to45,
                rowData.days45to60,
                rowData.days60to90,
                rowData.moreThan90,
                rowData.total
            ]);

            row.eachCell((cell, colNumber) => {
                if (colNumber > 1) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.numFmt = '#,##0.00';
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                }
                cell.border = thinBorder;
            });
        });

        // Add total row
        const totalRow = worksheet.addRow([
            'Total:',
            totalLessThan30,
            total30to45,
            total45to60,
            total60to90,
            totalMoreThan90,
            grandTotal
        ]);

        totalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
            if (colNumber === 1) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
                cell.numFmt = '#,##0.00';
            }
            cell.border = thinBorder;
        });

        worksheet.columns = [
            { width: 30 },  // Customer Name
            { width: 15 },  // < 30 Days
            { width: 15 },  // 30-45 Days
            { width: 15 },  // 45-60 Days
            { width: 15 },  // 60-90 Days
            { width: 15 },  // > 90 Days
            { width: 18 }   // Total
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    // Calculate totals
    const calculateTotals = () => {
        let totalLessThan30 = 0;
        let total30to45 = 0;
        let total45to60 = 0;
        let total60to90 = 0;
        let totalMoreThan90 = 0;
        let grandTotal = 0;

        agingData.forEach(customer => {
            totalLessThan30 += customer.lessThan30;
            total30to45 += customer.days30to45;
            total45to60 += customer.days45to60;
            total60to90 += customer.days60to90;
            totalMoreThan90 += customer.moreThan90;
            grandTotal += customer.total;
        });

        return { totalLessThan30, total30to45, total45to60, total60to90, totalMoreThan90, grandTotal };
    };

    // Filter data based on search query
    const filteredAgingData = agingData.filter((item) =>
        item.customerName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totals = calculateTotals();

    return (
        <div style={{
            minWidth: '100%',
            background: '#f5f7fa',
            padding: isMobile ? '8px' : '10px',
            position: 'relative',
            paddingBottom: isMobile ? '80px' : '10px'
        }}>
            {/* Loading Overlay */}
            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        zIndex: '99999 !important',
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress
                            size={60}
                            thickness={4}
                            sx={{
                                color: '#667eea',
                                mb: 2
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#667eea',
                                fontWeight: 600,
                                fontSize: '1rem'
                            }}
                        >
                            {loadingMessage}
                        </Typography>
                    </Box>
                </Box>
            )}

            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                padding: isMobile ? '8px' : '12px',
                position: 'relative',
                color: 'black',
                fontFamily: 'Times New Roman',
            }}>
                {/* Header Section */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    paddingBottom: '8px',
                    borderBottom: '2px solid #e0e0e0',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '8px' : '0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
                        <div style={{ width: '100%' }}>
                            <h4 style={{
                                fontSize: isMobile ? '15px' : '17px',
                                fontWeight: '700',
                                margin: 0,
                                letterSpacing: '-0.3px',
                                fontFamily: 'Times New Roman',
                                textTransform: 'uppercase',
                                textAlign: isMobile ? 'center' : 'left',
                                color: 'black'
                            }}>
                                Customer Ageing Report
                            </h4>
                        </div>
                    </div>
                </div>

                {/* Filters Row */}
                <div style={{
                    marginBottom: '12px',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        minWidth: 'fit-content',
                        paddingBottom: '4px',
                        flexWrap: 'nowrap'
                    }}>
                        {/* Search Input */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '140px' : '180px', flex: isMobile ? '1' : 'none' }}>
                            <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Search</label>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    borderRadius: '6px',
                                    backgroundColor: '#f8f9fa',
                                    fontSize: isMobile ? '11px' : '12px',
                                    height: '28px',
                                    border: '1px solid #d1d5db',
                                    padding: isMobile ? '4px 8px' : '4px 10px'
                                }}
                            />
                        </div>

                        {/* Buttons */}
                        {isMobile ? (
                            <>
                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchCustomerDueData}
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            width: '28px',
                                            height: '28px',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                            padding: '4px',
                                            marginTop: '18px'
                                        }}
                                    >
                                        <Refresh fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Export">
                                    <IconButton
                                        onClick={handleExportExcel}
                                        style={{
                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            color: 'white',
                                            width: '28px',
                                            height: '28px',
                                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)',
                                            padding: '4px',
                                            marginTop: '18px'
                                        }}
                                    >
                                        <FileDownload fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={fetchCustomerDueData}
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 16px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        minWidth: '80px',
                                        marginTop: '18px'
                                    }}
                                >
                                    Refresh
                                </Button>

                                <Button
                                    onClick={handleExportExcel}
                                    startIcon={<FileDownload />}
                                    disabled={agingData.length === 0}
                                    style={{
                                        background: agingData.length > 0 
                                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                                            : '#ccc',
                                        color: 'white',
                                        padding: '4px 16px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        boxShadow: agingData.length > 0 
                                            ? '0 2px 8px rgba(16, 185, 129, 0.4)' 
                                            : 'none',
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        minWidth: '120px',
                                        marginTop: '18px'
                                    }}
                                >
                                    Export to Excel
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Table Section */}
                <div style={{ marginBottom: isMobile ? '80px' : '10px' }}>
                    <TableContainer component={Paper} style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        borderRadius: '10px',
                        overflow: 'hidden'
                    }}>
                        <Table stickyHeader sx={{ minWidth: isMobile ? 1000 : 650 }} size="small" aria-label="customer ageing table">
                            <TableHead>
                                <TableRow style={{
                                    background: '#7b3f8f',
                                    height: '32px'
                                }}>
                                    {['Customer Name', '< 30 Days', '30-45 Days', '45-60 Days', '60-90 Days', '> 90 Days', 'Total'].map((col) => (
                                        <TableCell
                                            key={col}
                                            style={{
                                                background: '#7b3f8f',
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: isMobile ? '10px' : '11px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                padding: isMobile ? '6px 8px' : '6px 12px',
                                                textAlign: col === 'Customer Name' ? 'left' : 'right'
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredAgingData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                                No customer ageing records found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <>
                                        {filteredAgingData.map((data, index) => (
                                            <TableRow
                                                key={index}
                                                sx={{
                                                    background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                    '&:hover': {
                                                        backgroundColor: '#e3f2fd !important',
                                                        transition: 'all 0.15s ease-in-out'
                                                    },
                                                    borderBottom: '1px solid #e0e0e0'
                                                }}
                                            >
                                                <TableCell style={{ 
                                                    fontSize: isMobile ? '11px' : '12px', 
                                                    padding: isMobile ? '8px 8px' : '10px 12px', 
                                                    color: '#2d3748'
                                                }}>
                                                    {data.customerName}
                                                </TableCell>

                                                <TableCell style={{ 
                                                    fontSize: isMobile ? '11px' : '12px', 
                                                    padding: isMobile ? '8px 8px' : '10px 12px', 
                                                    color: '#2d3748',
                                                    textAlign: 'right'
                                                }}>
                                                    {data.lessThan30.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{ 
                                                    fontSize: isMobile ? '11px' : '12px', 
                                                    padding: isMobile ? '8px 8px' : '10px 12px', 
                                                    color: '#2d3748',
                                                    textAlign: 'right'
                                                }}>
                                                    {data.days30to45.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{ 
                                                    fontSize: isMobile ? '11px' : '12px', 
                                                    padding: isMobile ? '8px 8px' : '10px 12px', 
                                                    color: '#2d3748',
                                                    textAlign: 'right'
                                                }}>
                                                    {data.days45to60.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{ 
                                                    fontSize: isMobile ? '11px' : '12px', 
                                                    padding: isMobile ? '8px 8px' : '10px 12px', 
                                                    color: '#2d3748',
                                                    textAlign: 'right'
                                                }}>
                                                    {data.days60to90.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{ 
                                                    fontSize: isMobile ? '11px' : '12px', 
                                                    padding: isMobile ? '8px 8px' : '10px 12px', 
                                                    color: '#d32f2f',
                                                    textAlign: 'right',
                                                    fontWeight: '600'
                                                }}>
                                                    {data.moreThan90.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>

                                                <TableCell style={{ 
                                                    fontSize: isMobile ? '11px' : '12px', 
                                                    padding: isMobile ? '8px 8px' : '10px 12px', 
                                                    color: '#2d3748',
                                                    textAlign: 'right',
                                                    fontWeight: '600'
                                                }}>
                                                    {data.total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* Total Row */}
                                        <TableRow
                                            sx={{
                                                backgroundColor: '#f0f0f0',
                                                borderTop: '2px solid #cbd5e1',
                                                '&:hover': {
                                                    backgroundColor: '#f0f0f0 !important'
                                                }
                                            }}
                                        >
                                            <TableCell style={{ 
                                                fontSize: isMobile ? '11px' : '12px', 
                                                padding: isMobile ? '10px 8px' : '10px 12px', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                Total:
                                            </TableCell>

                                            <TableCell style={{ 
                                                fontSize: isMobile ? '11px' : '12px', 
                                                padding: isMobile ? '10px 8px' : '10px 12px', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                {totals.totalLessThan30.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{ 
                                                fontSize: isMobile ? '11px' : '12px', 
                                                padding: isMobile ? '10px 8px' : '10px 12px', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                {totals.total30to45.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{ 
                                                fontSize: isMobile ? '11px' : '12px', 
                                                padding: isMobile ? '10px 8px' : '10px 12px', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                {totals.total45to60.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{ 
                                                fontSize: isMobile ? '11px' : '12px', 
                                                padding: isMobile ? '10px 8px' : '10px 12px', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                {totals.total60to90.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{ 
                                                fontSize: isMobile ? '11px' : '12px', 
                                                padding: isMobile ? '10px 8px' : '10px 12px', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#d32f2f'
                                            }}>
                                                {totals.totalMoreThan90.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>

                                            <TableCell style={{ 
                                                fontSize: isMobile ? '11px' : '12px', 
                                                padding: isMobile ? '10px 8px' : '10px 12px', 
                                                textAlign: 'right',
                                                fontWeight: '700',
                                                color: '#2d3748'
                                            }}>
                                                {totals.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </div>
    );
};

export default CustomerAgingReport;
